import axios from 'axios';
import setAuthToken from './setAuthToken';
import { LSGO_AUTHENTICATION_BASE_URL, LSGO_AUTHENTICATION_CLIENT_ID, isProductionHost } from 'config/lsgoAuthentication';
import { randomBase64url, codeChallengeFor } from './lsgoAuthenticationRedirectFlow';

/**
 * lsgo-authentication sign-in for the SIS iframe routes (School Bytes /
 * Sentral embeds, routing/guards/SSORoute.js):
 *
 * 1. A hidden iframe opens /authorize?idp=…&prompt=none&embed=1. Auth sends
 *    it straight to the provider, where the user is already signed in at
 *    top level, and back to public/auth/silent-callback.html, which posts
 *    {code, state, error} to this window.
 * 2. This window exchanges the code with its in-memory PKCE verifier. The
 *    access AND refresh tokens stay in memory (embed=1 makes auth return the
 *    refresh token in the body; the cookie would be third-party here).
 * 3. The access token is refreshed with the refresh token shortly before it
 *    expires.
 * 4. The session is kept in sessionStorage per school (tenant), so the next
 *    iframe load in this tab (Sentral is server-rendered: every page reloads
 *    the iframe) reuses it instead of signing in again: no calls at all
 *    while the access token is valid, one /token refresh after that.
 *    sessionStorage is per tab (and, in a third-party iframe, per SIS site),
 *    so tabs never share or rotate each other's tokens, and it ends with the
 *    tab. A stored session is only reused within REUSE_WINDOW_MS of its last
 *    full sign-in with the provider; after that the provider is asked again,
 *    which bounds a shared computer where the next teacher signs in to the
 *    SIS in the same tab. If storage is unavailable, memory only (sign in on
 *    every load, as before).
 *
 * Why sessionStorage and not a cookie or localStorage, and every browser key
 * involved: lsgo-authentication docs/ARCHITECTURE.md, section 7.1.
 *
 * If the silent attempt fails (provider session expired, timeout), the
 * caller shows a button that runs the same flow in a popup (a popup must
 * come from a click).
 *
 * Which schools use this: utils/sisSchoolAuth.js (legacy's per-school flag,
 * plus the localStorage `lsgo_embed_auth_mode` = 'auth' override for testing,
 * which is ignored in production).
 */

export const EMBED_AUTH_MODE_KEY = 'lsgo_embed_auth_mode';
export const SILENT_CALLBACK_PATH = '/auth/silent-callback.html';
const MESSAGE_TYPE = 'lsgo-auth';
const SILENT_TIMEOUT_MS = 8000;
const POPUP_TIMEOUT_MS = 5 * 60 * 1000;
const REFRESH_LEAD_SECONDS = 60;
const STORAGE_KEY_PREFIX = 'lsgo_embed_session:';
const REUSE_WINDOW_MS = 60 * 60 * 1000;

/** SSORoute's ssoType -> lsgo-authentication provider_key. */
const IDP_BY_SSO_TYPE = { schoolBytes: 'schoolbytes', sentral: 'sentral' };

export function isFramed() {
    try {
        return window.self !== window.top;
    } catch {
        return true; // cross-origin top: framed
    }
}

/** The testing override: never in production, where only legacy's per-school authMode counts. */
export function embedAuthEnabled() {
    try {
        if (isProductionHost()) return false;
        return window.localStorage.getItem(EMBED_AUTH_MODE_KEY) === 'auth';
    } catch {
        return false;
    }
}

export function idpForSsoType(ssoType) {
    return IDP_BY_SSO_TYPE[ssoType] ?? null;
}

function redirectUri() {
    return `${window.location.origin}${SILENT_CALLBACK_PATH}`;
}

/** `extra`: further /authorize parameters (LTI: lti_launch / lti_link, src/containers/LTI/LtiLaunch.js). */
async function newAttempt({ idp, tenant, silent, extra = {} }) {
    const verifier = randomBase64url(32);
    const challenge = await codeChallengeFor(verifier);
    const state = randomBase64url(16);
    const params = new URLSearchParams({
        response_type: 'code',
        client_id: LSGO_AUTHENTICATION_CLIENT_ID,
        redirect_uri: redirectUri(),
        code_challenge: challenge,
        code_challenge_method: 'S256',
        state,
        scope: 'openid profile',
        embed: '1',
    });
    if (idp) params.set('idp', idp);
    if (tenant) params.set('tenant', tenant);
    for (const [name, value] of Object.entries(extra)) params.set(name, value);
    if (silent) params.set('prompt', 'none');
    return { verifier, state, url: `${LSGO_AUTHENTICATION_BASE_URL}/authorize?${params.toString()}` };
}

/**
 * Resolves with the code from the one window we opened, for our one state.
 * Anything else (other origins, other windows, replayed states) is ignored.
 * Exported for tests.
 */
export function waitForCode({ source, state, timeoutMs, win = window }) {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            cleanup();
            reject(new Error('timeout'));
        }, timeoutMs);
        function onMessage(event) {
            const data = event.data;
            if (event.origin !== win.location.origin) return;
            if (!source() || event.source !== source()) return;
            if (!data || data.type !== MESSAGE_TYPE || data.state !== state) return;
            cleanup();
            if (data.error || !data.code) reject(new Error(data.error || 'no_code'));
            else resolve(data.code);
        }
        function cleanup() {
            clearTimeout(timer);
            win.removeEventListener('message', onMessage);
        }
        win.addEventListener('message', onMessage);
    });
}

async function exchangeCode(code, verifier) {
    const res = await axios.post(
        `${LSGO_AUTHENTICATION_BASE_URL}/token`,
        new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: LSGO_AUTHENTICATION_CLIENT_ID,
            code,
            redirect_uri: redirectUri(),
            code_verifier: verifier,
        }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
    );
    return res.data;
}

// --- Session (memory, mirrored to sessionStorage per tenant) -----------------

let session = null; // { accessToken, refreshToken, expiresAt, tenant, signedInAt, timer }
let onExpired = null;

function storageKey(tenant) {
    return `${STORAGE_KEY_PREFIX}${tenant}`;
}

function readStored(tenant) {
    try {
        const raw = window.sessionStorage.getItem(storageKey(tenant));
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function writeStored({ accessToken, refreshToken, expiresAt, tenant, signedInAt }) {
    if (!tenant) return;
    try {
        window.sessionStorage.setItem(
            storageKey(tenant),
            JSON.stringify({ accessToken, refreshToken, expiresAt, tenant, signedInAt })
        );
    } catch {
        // Storage unavailable: memory only.
    }
}

function removeStored(tenant) {
    if (!tenant) return;
    try {
        window.sessionStorage.removeItem(storageKey(tenant));
    } catch {
        // Storage unavailable: nothing stored.
    }
}

function clearTimer() {
    if (session?.timer) clearTimeout(session.timer);
}

function scheduleRefresh() {
    const delayMs = Math.max(5000, session.expiresAt - REFRESH_LEAD_SECONDS * 1000 - Date.now());
    if (session.refreshToken) session.timer = setTimeout(refresh, delayMs);
}

/** `signedInAt`: when the provider last confirmed this user (a full sign-in), kept across refreshes. */
function applyTokens(
    { access_token: accessToken, refresh_token: refreshToken, expires_in: expiresIn },
    { tenant, signedInAt }
) {
    clearTimer();
    // Same header format as LsgoAuthenticationCallback; never written to localStorage.
    setAuthToken(accessToken, { persist: false });
    session = {
        accessToken,
        refreshToken: refreshToken ?? session?.refreshToken ?? null,
        expiresAt: Date.now() + (Number(expiresIn) || 600) * 1000,
        tenant: tenant ?? null,
        signedInAt,
        timer: null,
    };
    writeStored(session);
    scheduleRefresh();
    return accessToken;
}

async function refreshWith(refreshToken) {
    const res = await axios.post(
        `${LSGO_AUTHENTICATION_BASE_URL}/token`,
        new URLSearchParams({
            grant_type: 'refresh_token',
            client_id: LSGO_AUTHENTICATION_CLIENT_ID,
            refresh_token: refreshToken,
        }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
    );
    return res.data;
}

async function refresh() {
    if (!session?.refreshToken) return;
    const { tenant, signedInAt } = session;
    try {
        applyTokens(await refreshWith(session.refreshToken), { tenant, signedInAt });
    } catch {
        clearEmbedSession();
        if (onExpired) onExpired();
    }
}

/**
 * Picks up this tab's stored session for `tenant`, if it is still within the
 * reuse window: as is while the access token is valid, else refreshed once.
 * Resolves with the access token, or null (then sign in as normal).
 */
export async function restoreEmbedSession({ tenant }) {
    if (!tenant) return null;
    const stored = readStored(tenant);
    if (!stored?.accessToken || stored.tenant !== tenant || !(Date.now() - stored.signedInAt < REUSE_WINDOW_MS)) {
        removeStored(tenant);
        return null;
    }
    if (stored.expiresAt - Date.now() > REFRESH_LEAD_SECONDS * 1000) {
        clearTimer();
        setAuthToken(stored.accessToken, { persist: false });
        session = { ...stored, timer: null };
        scheduleRefresh();
        return stored.accessToken;
    }
    if (!stored.refreshToken) {
        removeStored(tenant);
        return null;
    }
    try {
        return applyTokens(await refreshWith(stored.refreshToken), { tenant, signedInAt: stored.signedInAt });
    } catch {
        // Rotated elsewhere (a duplicated tab), revoked or expired: sign in again.
        removeStored(tenant);
        return null;
    }
}

/** Called when the in-memory session can't be refreshed any more. */
export function onEmbedSessionExpired(callback) {
    onExpired = callback;
}

export function hasEmbedSession() {
    return Boolean(session?.accessToken);
}

/** The current access token, for calls to lsgo-authentication itself (e.g. /lti/*). */
export function embedAccessToken() {
    return session?.accessToken ?? null;
}

function clearEmbedSession() {
    clearTimer();
    removeStored(session?.tenant);
    session = null;
}

/**
 * Logout: revoke the refresh token (always 200) and forget it. With
 * credentials, so auth also clears its last-sign-in record where the
 * browser keeps it.
 */
export async function endEmbedSession() {
    const refreshToken = session?.refreshToken;
    clearEmbedSession();
    if (!refreshToken) return;
    await axios
        .post(`${LSGO_AUTHENTICATION_BASE_URL}/token/revoke`, new URLSearchParams({ token: refreshToken }), {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            withCredentials: true,
        })
        .catch(() => {});
}

// --- Sign-in ------------------------------------------------------------------

/** Silent attempt in a hidden iframe. Resolves with the access token, or rejects. */
export async function silentSignIn({ idp, tenant, extra }) {
    const attempt = await newAttempt({ idp, tenant, silent: true, extra });
    const frame = document.createElement('iframe');
    frame.style.display = 'none';
    frame.setAttribute('aria-hidden', 'true');
    frame.title = 'Signing in';
    const waiting = waitForCode({ source: () => frame.contentWindow, state: attempt.state, timeoutMs: SILENT_TIMEOUT_MS });
    frame.src = attempt.url;
    document.body.appendChild(frame);
    try {
        const code = await waiting;
        return applyTokens(await exchangeCode(code, attempt.verifier), { tenant, signedInAt: Date.now() });
    } finally {
        frame.remove();
    }
}

/**
 * Popup fallback. MUST be called directly from a click handler: the popup is
 * opened synchronously (blank) before any await, or browsers block it.
 */
export function popupSignIn({ idp, tenant, extra }) {
    const popup = window.open('about:blank', 'lsgo-sign-in', 'width=520,height=680');
    if (!popup) return Promise.reject(new Error('popup_blocked'));
    return (async () => {
        const attempt = await newAttempt({ idp, tenant, silent: false, extra });
        const waiting = waitForCode({ source: () => popup, state: attempt.state, timeoutMs: POPUP_TIMEOUT_MS });
        popup.location.href = attempt.url;
        try {
            const code = await waiting;
            return applyTokens(await exchangeCode(code, attempt.verifier), { tenant, signedInAt: Date.now() });
        } finally {
            if (!popup.closed) popup.close();
        }
    })();
}
